import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Gift, Sparkles, PartyPopper } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const ACK_KEY = "jps_ack_version";
const ACK_VERSION = "2026-07-13";
const SEEN_KEY = "jps_promo_seen";

export const PromoPopup = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [sale, setSale] = useState(null);
  const [rewards, setRewards] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/settings/sale").then((r) => r.data).catch(() => ({})),
      api.get("/settings/rewards").then((r) => r.data).catch(() => ({})),
    ]).then(([s, r]) => { setSale(s); setRewards(r); });
  }, []);

  useEffect(() => {
    if (loading || sale === null || rewards === null) return;
    if (sessionStorage.getItem(SEEN_KEY)) return;

    const saleActive = !!sale?.active;
    const rewardsOn = !!rewards?.enabled;
    if (!saleActive && !rewardsOn) return;      // nothing to promote
    if (user && !saleActive) return;            // don't nag logged-in users year-round

    let cancelled = false;
    let tries = 0;
    const tick = () => {
      if (cancelled) return;
      if (localStorage.getItem(ACK_KEY) === ACK_VERSION) {
        setTimeout(() => {
          if (cancelled) return;
          setOpen(true);
          sessionStorage.setItem(SEEN_KEY, "1");
        }, 700);
        return;
      }
      if (tries++ < 60) setTimeout(tick, 500);
    };
    tick();
    return () => { cancelled = true; };
  }, [loading, user, sale, rewards]);

  const saleActive = !!sale?.active;
  const rewardLine = rewards?.enabled
    ? `Earn $${Number(rewards.earn_reward).toFixed(2)} back for every $${Number(rewards.earn_spend).toFixed(2)} you spend — redeem it for money off future orders.`
    : "";

  const goShop = () => { setOpen(false); navigate("/shop"); };
  const goSignup = () => { setOpen(false); navigate("/auth"); };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-md overflow-hidden" data-testid="promo-popup">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-brand-teal/10 to-transparent" />
        <DialogHeader>
          <div className="mx-auto mb-1 flex h-14 w-14 items-center justify-center rounded-full bg-brand-teal/15">
            {saleActive ? <PartyPopper className="h-7 w-7 text-brand-teal" /> : <Gift className="h-7 w-7 text-brand-teal" />}
          </div>
          <DialogTitle className="text-center font-display text-2xl text-brand-navy" data-testid="promo-popup-title">
            {saleActive ? (sale.banner || "A sale is on now") : "Earn rewards on every order"}
          </DialogTitle>
          <DialogDescription className="text-center text-brand-navy/70">
            {saleActive && rewardLine ? (
              <>Shop the sale now.<br />{rewardLine}</>
            ) : saleActive ? "Shop the sale now while it lasts." : rewardLine}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex-col gap-2 sm:flex-col">
          <Button onClick={goShop} className="w-full rounded-full bg-brand-navy hover:bg-brand-blue text-white gap-2" data-testid="promo-popup-shop">
            <Sparkles className="h-4 w-4" /> {saleActive ? "Shop the sale" : "Start shopping"}
          </Button>
          {!user && (
            <Button onClick={goSignup} variant="outline" className="w-full rounded-full" data-testid="promo-popup-signup">
              Sign up &amp; start earning
            </Button>
          )}
          <button onClick={() => setOpen(false)} className="mx-auto text-xs text-brand-navy/50 underline hover:text-brand-navy" data-testid="promo-popup-dismiss">
            No thanks
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
